import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { IBaseProps, ILocation } from '@rbi-ctg/frontend';
import useEffectOnce from 'hooks/use-effect-once';
import { OnAppStateChange, useUpdateOnAppStateChange } from 'hooks/use-update-on-app-state-change';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLogger } from 'state/logger';
import { useMParticleContext } from 'state/mParticle';
import { RBIExpandedPlatform, RBIPlatform, checkPlatform, isNativeIOS } from 'utils/environment';
import {
  GeolocationPermissionStates,
  PermissionState,
  checkIfLocationPermissionIsPreapproved,
  determinePermissionsFromGetCurrentPositionErrorCode,
  getUsersCurrentPosition
} from 'utils/geolocation';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import noop from 'utils/noop';
import { DeviceType, radarTrackOnce } from 'utils/radar';

import { useAuthContext } from '../auth';

export interface IGeolocationCtx {
  loadCurrentPosition: () => Promise<ILocation | undefined>;
  userCoordinates: ILocation | null;
  setUserCoordinates: React.Dispatch<React.SetStateAction<ILocation>>;
  permission: GeolocationPermissionStates;
  setPermission: (permissionState: GeolocationPermissionStates) => void;
}

export const GeolocationContext = createContext<IGeolocationCtx>({
  loadCurrentPosition: noop as any,
  userCoordinates: null,
  setUserCoordinates: noop,
  permission: GeolocationPermissionStates.PROMPT,
  setPermission: noop
});

export const useGeolocation = () => useContext(GeolocationContext);

export const useGeolocationPermission = () => {
  const ctx = useGeolocation();
  return ctx.permission;
};

export const GeolocationProvider = ({ children }: IBaseProps) => {
  const auth = useAuthContext();
  const { deviceId } = useMParticleContext();
  const enableRadarTracking = useFlag(LaunchDarklyFlag.ENABLE_RADAR_TRACKING);
  const logger = useLogger();

  const [userCoordinates, setUserCoordinates] = useState<ILocation | null>(null);
  const [permission, setPermissionState] = useState<GeolocationPermissionStates>(
    GeolocationPermissionStates.PROMPT
  );
  const userId = auth.user?.cognitoId || LocalStorage?.getItem(StorageKeys.USER)?.cognitoId;

  const setPermission = useCallback(
    (permissionState: GeolocationPermissionStates) => {
      if (permissionState !== permission) {
        setPermissionState(permissionState);
      }
    },
    [permission]
  );

  useEffect(() => {
    if (permission !== GeolocationPermissionStates.PROMPT) {
      return;
    }

    checkIfLocationPermissionIsPreapproved()
      .then((permissionState: GeolocationPermissionStates) => {
        setPermission(permissionState);
      })
      .catch(permissionState => {
        /** permissionState can be either 'denied' or 'prompt' state here. */
        setPermission(permissionState);
      });
  }, [permission, setPermission]);

  const { lat, lng } = userCoordinates ?? {};

  const loadCurrentPosition = useCallback(() => {
    return getUsersCurrentPosition()
      .then(location => {
        if (!location || !location.lat || !location.lng) {
          return Promise.reject({ code: PermissionState.DENIED });
        }

        // only update coords if they're not yet set or they have changed
        if (lat !== location.lat || lng !== location.lng) {
          // also let's only call radar if we're actually changing the user's coords
          if (enableRadarTracking && userId) {
            let deviceType: DeviceType = RBIExpandedPlatform.WEB;

            if (checkPlatform(RBIPlatform.APP)) {
              deviceType = isNativeIOS() ? RBIExpandedPlatform.IOS : RBIExpandedPlatform.ANDROID;
            }

            radarTrackOnce(location, userId, deviceId, deviceType);
          }

          setUserCoordinates({
            lat: location.lat,
            lng: location.lng
          });
        }

        return location;
      })
      .catch(error => {
        logger.error({ error, message: 'Error geolocating user' });
        if (error?.code) {
          setPermission(determinePermissionsFromGetCurrentPositionErrorCode(error.code));
          return undefined;
        }
        throw error;
      });
  }, [lat, lng, enableRadarTracking, userId, deviceId, logger, setPermission]);

  useEffect(() => {
    if (permission === GeolocationPermissionStates.GRANTED) {
      loadCurrentPosition();
    }
    // eslint-disable-next-line
  }, [permission]);

  const checkPermissionsAndUpdate = useCallback(async () => {
    const returnedPermissions = await checkIfLocationPermissionIsPreapproved();
    if (returnedPermissions === GeolocationPermissionStates.GRANTED) {
      // only persist permissions at this point
      // if the user has prev granted location
      // if not, we want to re-prompt later on store locator
      setPermission(returnedPermissions);
      loadCurrentPosition();
    }
  }, [loadCurrentPosition, setPermission]);

  const maybeGetLocationAndUpdate: OnAppStateChange = useCallback(
    async ({ isActive }) => {
      if (isActive) {
        checkPermissionsAndUpdate();
      }
    },
    [checkPermissionsAndUpdate]
  );

  useEffectOnce(() => {
    checkPermissionsAndUpdate();
  });

  // get user's current location permissions when app becomes active
  useUpdateOnAppStateChange(maybeGetLocationAndUpdate);

  return (
    <GeolocationContext.Provider
      value={{
        permission,
        loadCurrentPosition,
        userCoordinates,
        setUserCoordinates,
        setPermission
      }}
    >
      {children}
    </GeolocationContext.Provider>
  );
};
